import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Filter, Star, Newspaper } from 'lucide-react';
import { Button } from '../components/ui/button';
import AdSenseAd from '../components/AdSenseAd';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://shelovesthee.com';
const API = `${BACKEND_URL}/api`;

const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);

  const categories = ['All', 'Jackson State', 'SWAC', 'HBCU', 'Football', 'Basketball', 'Recruiting'];

  // SEO metadata for the news page
  const pageMeta = {
    title: 'HBCU Sports News | SheLovesThee',
    description: 'Latest HBCU sports news, SWAC football updates, Jackson State coverage, and Black college athletics. Breaking news and analysis from SheLovesThee.',
    url: `${SITE_URL}/news`,
    image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/12mdvn7w_9ABE93F3-1197-42CC-98EE-6245B2328755.png'
  };

  // Featured interactive articles - prominently displayed
  const featuredInteractiveArticles = [
    {
      id: 'jsu-basketball-2026-27',
      title: 'RUFFIN RETURNS: JACKSON STATE BASKETBALL RELOADS FOR 2026-27',
      excerpt: 'Daeshun Ruffin returns as Jackson State prepares for SWAC Media Day. Full roster, coaches, and 2026-27 schedule.',
      image: 'https://gojsutigers.com/images/2023/10/14/Ruffin__Daeshun_2324_MBB.jpg',
      category: 'Basketball',
      link: '/jsu-basketball-2026-27',
      badge: 'INTERACTIVE'
    },
    {
      id: 'boombox-classic',
      title: 'JACKSON STATE DOMINATES SOUTHERN IN 2026 BOOMBOX CLASSIC',
      excerpt: 'JSU defeats Southern 35-17 in front of nearly 40,000 fans at Veterans Memorial Stadium',
      image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/v0qn633b_IMG_1555.webp',
      category: 'Jackson State',
      link: '/boombox-classic',
      badge: 'INTERACTIVE'
    },
    {
      id: 'jsu-soccer',
      title: 'JACKSON STATE SOCCER: 2026 SEASON PREVIEW',
      excerpt: 'Meet the Tigers as they prepare for another competitive season in SWAC soccer',
      image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/npvdmwbc_IMG_9417.jpeg',
      category: 'Jackson State',
      link: '/jackson-state-soccer',
      badge: 'INTERACTIVE'
    }
  ];

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const url = category && category !== 'All' 
          ? `${API}/articles?category=${category}&limit=50`
          : `${API}/articles?limit=50`;
        const response = await axios.get(url);
        setArticles(response.data);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [category]);

  return (
    <>
      <Helmet>
        <title>{pageMeta.title}</title>
        <meta name="description" content={pageMeta.description} />
        <link rel="canonical" href={pageMeta.url} />
        <meta property="og:title" content={pageMeta.title} />
        <meta property="og:description" content={pageMeta.description} />
        <meta property="og:url" content={pageMeta.url} />
        <meta property="og:image" content={pageMeta.image} />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={pageMeta.title} />
        <meta name="twitter:description" content={pageMeta.description} />
        <meta name="twitter:image" content={pageMeta.image} />
      </Helmet>
      <div className="min-h-screen bg-slate-50" data-testid="news-page">
      <div className="bg-[#001f3f] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight uppercase heading-font" data-testid="news-page-title">
            HBCU News
          </h1>
          <p className="text-xl text-slate-300 mt-4">Stay updated with the latest from SWAC and HBCU sports</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Featured Interactive Articles Section */}
        <div className="mb-10" data-testid="featured-interactive-section">
          <div className="flex items-center space-x-2 mb-4">
            <Star className="h-5 w-5 text-[#FFD700]" fill="#FFD700" />
            <span className="text-sm font-bold uppercase tracking-wider text-slate-700">Featured Interactive Stories</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featuredInteractiveArticles.map(article => (
              <Link
                key={article.id}
                to={article.link}
                className="group relative bg-[#001f3f] overflow-hidden rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300"
                data-testid={`featured-article-${article.id}`}
              >
                <div className="absolute inset-0">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-40 group-hover:scale-105 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#001f3f] via-[#001f3f]/70 to-transparent" />
                </div>
                <div className="relative p-6 h-64 flex flex-col justify-end">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#FFD700] bg-[#001f3f]/80 px-2 py-1 rounded">
                      {article.badge}
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#007AFF]">
                      {article.category}
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold uppercase text-white heading-font mb-2 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-slate-300 line-clamp-2">{article.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="ad-unit mb-8" data-testid="news-banner-ad">
          <AdSenseAd 
            adSlot="2800195719"
            adFormat="horizontal"
            style={{ minHeight: '90px' }}
            label="Advertisement"
          />
        </div>

        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="h-5 w-5 text-slate-600" />
            <span className="text-sm font-bold uppercase tracking-wider text-slate-600">Filter by Category</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <Button
                key={cat}
                onClick={() => setCategory(cat === 'All' ? '' : cat)}
                className={`px-4 py-2 rounded-full font-bold uppercase text-sm ${
                  (cat === 'All' && !category) || cat === category
                    ? 'bg-[#007AFF] text-white'
                    : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
                }`}
                data-testid={`category-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-center space-x-2 mb-4">
          <Newspaper className="h-5 w-5 text-slate-600" />
          <span className="text-sm font-bold uppercase tracking-wider text-slate-600">Latest Articles</span>
        </div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading">Loading articles...</div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12 text-slate-600" data-testid="no-articles">
            No articles found. Check back soon!
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map(article => (
              <ArticleCard key={article.article_id} article={article} />
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

const ArticleCard = ({ article }) => {
  // Use redirect_url if available, otherwise use standard article path
  const articleLink = article.redirect_url || `/news/${article.article_id}`;
  
  return (
    <Link 
      to={articleLink}
      className="group bg-white border border-slate-200 hover:border-[#007AFF] transition-all duration-300 shadow-sm hover:shadow-xl overflow-hidden"
      data-testid={`article-card-${article.article_id}`}
    >
      {article.image_url && (
        <div className="h-48 overflow-hidden">
          <img 
            src={article.image_url} 
            alt={article.title} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold uppercase tracking-wider text-[#007AFF]">
            {article.category}
          </span>
          {article.is_premium && (
            <span className="text-xs font-bold uppercase tracking-wider text-[#DC2626] bg-[#001f3f] px-2 py-1">
              Members
            </span>
          )}
          {article.redirect_url && (
            <span className="text-xs font-bold uppercase tracking-wider text-[#FFD700] bg-[#001f3f] px-2 py-1">
              Interactive
            </span>
          )}
        </div>
        <h3 className="text-xl font-bold uppercase mb-2 heading-font text-[#001f3f] line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-slate-600 line-clamp-3 mb-4">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>{new Date(article.created_at).toLocaleDateString()}</span>
        </div>
      </div>
    </Link>
  );
};

export default NewsPage;